import React, { useEffect, useRef, useState } from 'react';

import moment from 'moment';

import { CheckRounded, InfoOutlined } from '@mui/icons-material';
import { Box, Snackbar } from '@mui/material';

import Alert from '../../components/Alert';
import AddCoSettlementAgent from '../../components/Dialogs/AddCoSettlmentAgent';
import BasicConfirm from '../../components/Dialogs/BasicConfirm';
import LoanCloser from '../../components/Dialogs/LoanCloser';
import OrderDetailsPopover from '../../components/Dialogs/OrderDetailsPopover';
import ProductModal from '../../components/Dialogs/ProductModal';
import UploadInkSignedDocument from '../../components/Dialogs/UploadInkSignedDocument';
import ViewHistory from '../../components/Dialogs/ViewHistory';
import OptionalActions from '../../components/OptionalActions';
import OrderDetailsAccordion from '../../components/OrderDetailsAccordion/OrderDetailsAccordion';
import OrderDetailsHeader from '../../components/OrderDetailsHeader';
import SolexHybrideCloseActions from '../../components/SolexHybrideCloseActions/SolexHybrideCloseActions';
import Loading from '../../components/loading';
import { OrderStatus } from '../../constants/OrderStatus';
import { black } from '../../constants/colors';
import { docTagProduct } from '../../constants/docEditorWalkThru';
import { DocGenerationSource } from '../../constants/docGenerationSource';
import { OrderAction } from '../../constants/orderActions';
import useExtractTagDocs from '../../hooks/useExtractTagDocs';
import useOrderActions from '../../hooks/useOrderActions';
import useOrderReloader from '../../hooks/useOrderReloader';
import useOrderStates from '../../hooks/useOrderStates';
import { useAppSelector } from '../../redux-store/hooks';
import { closeSnackbar } from '../../redux-store/notifications/notificationsSlice';
import {
  UploadDocumentsOrderAsync,
  fetchSettlementAgentAsync,
  setAlert,
} from '../../redux-store/orders/ordersSlice';
import { RootState } from '../../redux-store/store';
import { IItemStatus, IOrderStatus, IPopoverContent } from '../../type';
import { fileUploader } from '../../utils/fileUploader';

export default function HybridSALandingPage() {
  // custom hooks
  const {
    navigate,
    dispatch,
    scrollToOrder,
    scrollToDocument,
    params,
    orderDetailsRef,
    documentRef,
    form,
    input,
    order,
    orders,
    isFetching,
    isFetchingText,
    alertUser,
    account,
    showAddCoSABtn,
    isOrderReviewedBySA,
    isDocumentReviewedBySA,
    isCurrentUserEscrowOnOrder,
    isCurrentUserEscrowOfficerOnOrder,
    existingSettlementAgentsOnTheOrder,
    isThirdPartyOrder,
    orderStatus,
    currentUserName,
    historiesToShow,
  } = useOrderStates();
  useOrderReloader(order);
  const { docsTagged, docsEdited, tagDocsLink } = useExtractTagDocs(
    order,
    currentUserName
  );
  const {
    getToken,
    goResetLogin,
    handleAddCosettlementAgents,
    handleUpdateOrderActions,
    handleCancelConfirmActions,
  } = useOrderActions(order, currentUserName);

  // Page Local States
  const [partyPanel, setPartyPanel] = useState<boolean>(false);
  const [settlementPanel, setSettlementPanel] = useState<boolean>(false);
  const [orderDetailsPanel, setOrderDetailsPanel] = useState<boolean>(false);
  const [downloadPanel, setDownloadPanel] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [openSettlementHistory, setOpenSettlementHistory] =
    useState<boolean>(false);
  const [openReport, setOpenReport] = useState<boolean>(false);
  const [arrowRef, setArrowRef] = useState<any>(null);
  const [orderZIndex, setOrderZIndex] = useState<number>(0);
  const [onClosingZIndex, setOnClosingZIndex] = useState<number>(0);
  const [documentZIndex, setDocumentZIndex] = useState<number>(0);
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [orderStatusToShow, setOrderStatusToShow] = useState<IOrderStatus>({
    name: 'IN PROGRESS',
    color: 'warning',
  });
  const [openUploadDocs, setOpenUploadDocs] = useState<boolean>(false);
  const [uploadSelectError, setUploadSelectError] = useState<string | null>(
    null
  );
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [openTagDocsModal, setOpenTagDocsModal] =
    React.useState<boolean>(false);
  const [saToken, setSaToken] = useState<string>('');
  const [showCancelConfirmation, setShowCancelConfirmation] =
    useState<boolean>(false);
  const [orderExpired, setOrderExpired] = useState<boolean>(false);
  const [openAddCoSa, setOpenAddCoSa] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const onClosingRef = useRef<HTMLDivElement | null>(null);

  const initialPopoverContent: IPopoverContent = {
    title: '',
    firstBtnText: 'I HAVE REVIEWED',
    secondBtnText: '',
  };
  const [popoverContent, setPopoverContent] = useState<IPopoverContent>(
    initialPopoverContent
  );
  const [openPopover, setOpenPopover] = useState<any>(false);
  const [existingCoSa, setExcistingCoSa] = useState<any>(null);

  // Redux States
  const selected: any = useAppSelector(
    (state: RootState) => state.orders.order?.OrderDetails?.Documents
  );
  const isAllInkSignDocumentsPrinted: any =
    order?.Actions?.PreClosing_SettlementAgent_PrintAllInkSignDocuments;
  const isFacilitateInkSignConfirmed: any =
    order?.Actions
      ?.PreClosing_SettlementAgent_ConfirmFacilitateSigningInkSignDocuments ??
    false;
  const isInkSignedDocsUploadedToVault: any =
    order?.Actions
      ?.PostClosing_SettlementAgent_UploadInkSignedDocumentsToVault ?? false;
  const isFaOrder = order?.OrderType?.DocGen === DocGenerationSource.FA;

  const handleUploadFiles = (files: File[]) => {
    if (files.length == 0) return;
    const { error, uploadArray, documentNamesArray } = fileUploader(
      files,
      selected,
      order
    );
    if (error) {
      setUploadSelectError(error);
    } else {
      setUploadSelectError(null);
      dispatch(
        UploadDocumentsOrderAsync({
          id: order.Id,
          solexSessionId: order.SolexSessionId,
          documentNamesArray: documentNamesArray,
          documentDataArray: uploadArray,
          signingType: 'InkSign',
        })
      ).then((response) => {
        if (!response.type.includes('pending')) {
          setError(null);
          setFileName('');
          setUploadedFile(null);
          setOpenUploadDocs(false);
          if (!isInkSignedDocsUploadedToVault) {
            handleUpdateOrderActions(
              OrderAction.PostClosing_SettlementAgent_UploadInkSignedDocumentsToVault,
              true
            );
          }
        }
      });
    }
  };

  useEffect(() => {
    if (moment() > moment(order?.ExpireDate)) {
      setOrderExpired(true);
    }
  }, [order]);

  useEffect(() => {
    if (
      orderStatus === OrderStatus.OpenAllSigned &&
      isAllInkSignDocumentsPrinted &&
      isFacilitateInkSignConfirmed
    ) {
      if (order.OrderType.DocGen === 'CX' && isInkSignedDocsUploadedToVault) {
        setOrderStatusToShow({
          name: 'COMPLETE',
          color: 'success',
        });
      } else if (order.OrderType.DocGen === 'FA') {
        setOrderStatusToShow({
          name: 'COMPLETE',
          color: 'success',
        });
      }
    }
  }, [
    orderStatus,
    isAllInkSignDocumentsPrinted,
    isFacilitateInkSignConfirmed,
    isInkSignedDocsUploadedToVault,
  ]);

  useEffect(() => {
    return () => {
      dispatch(setAlert(null));
      dispatch(closeSnackbar());
    };
  }, [dispatch]);

  useEffect(() => {
    const fetchSAAccessToken = async () => {
      if (tagDocsLink) {
        getToken(tagDocsLink).then((response: any) => {
          if (response && response.payload.length > 0) {
            setSaToken(response.payload);
          }
        });
      }
    };
    if (tagDocsLink && saToken.length === 0) {
      fetchSAAccessToken();
    }
  }, [tagDocsLink]);

  const openTagDocsLink = () => {
    setOpenTagDocsModal(false);
    if (tagDocsLink) {
      window.open('', 'thisWindow');
      form.current?.submit();
      handleUpdateOrderActions(
        OrderAction.PostClosing_SettlementAgent_UploadAdditionaldocuments,
        true
      );
    }
  };

  const onSaActionsClick = (name: string, item: IItemStatus, index: number) => {
    if (name === 'Pre Closing') {
      switch (index) {
        case 0: {
          setAnchorEl(orderDetailsRef.current);
          scrollToOrder();
          setOrderDetailsPanel(true);
          setPartyPanel(true);
          setDownloadPanel(false);
          setOnClosingZIndex(0);
          if (!isOrderReviewedBySA) {
            setOpenPopover(true);
            setPopoverContent({
              title:
                'Please review the Order Details and Party Information for accuracy. When complete, please click I HAVE REVIEWED.',
              firstBtnText: 'I HAVE REVIEWED',
              secondBtnText: order.OrderDetails.LoanCloser.EmailAddress
                ? 'INACCURATE INFORMATION'
                : '',
              action: goSAReviewOrder,
            });
            setShowOverlay(true);
            setOrderZIndex(97);
            setDocumentZIndex(0);
          }
          break;
        }
        case 1: {
          setAnchorEl(documentRef.current);
          scrollToDocument();
          setDownloadPanel(true);
          setOrderDetailsPanel(false);
          setPartyPanel(false);
          if (!isDocumentReviewedBySA) {
            setOpenPopover(true);
            setPopoverContent({
              title:
                'Please review the Order documents for accuracy. When complete, please click I HAVE REVIEWED.',
              firstBtnText: 'I HAVE REVIEWED',
              secondBtnText: order.OrderDetails.LoanCloser.EmailAddress
                ? 'INACCURATE INFORMATION'
                : '',
              action: goSAReviewDocuments,
            });
            setShowOverlay(true);
            setDocumentZIndex(97);
            setOrderZIndex(0);
          }
          break;
        }
        case 2: {
          setOpenTagDocsModal(true);
          setDownloadPanel(true);
          break;
        }
        case 3: {
          setAnchorEl(documentRef.current);
          scrollToDocument();
          setDownloadPanel(true);
          setOrderDetailsPanel(false);
          setPartyPanel(false);
          setOpenPopover(true);
          setPopoverContent({
            title: isFaOrder
              ? 'Please click View/Print All below to print all documents.'
              : 'Please click View/Print Ink Sign below to print all Ink Sign documents.',
            firstBtnText: '',
            secondBtnText: '',
          });
          if (!isAllInkSignDocumentsPrinted) {
            setShowOverlay(true);
            setDocumentZIndex(97);
            setOnClosingZIndex(0);
          }
          break;
        }
      }
    } else if (name === 'On Closing') {
      if (item.name === 'Facilitate signing of Ink Sign Documents *') {
        if (!isFacilitateInkSignConfirmed) {
          setAnchorEl(onClosingRef.current);
          setShowOverlay(true);
          setOnClosingZIndex(97);
          setDocumentZIndex(0);
          setOpenPopover(true);
          setPopoverContent({
            title:
              'Please confirm that required Ink Sign Documents have been signed by all parties.',
            firstBtnText: 'I Confirm',
            secondBtnText: '',
            action: goSAConfirmFacilitateInkSignDocs,
          });
        }
      }
    } else if (name === 'Post Closing') {
      switch (index) {
        case 0: {
          setOpenUploadDocs(true);
          break;
        }
      }
    }
  };

  const reportInaccurateInformation = () => {
    setShowOverlay(false);
    setOpenReport(true);
  };

  const goSAReviewOrder = () => {
    setShowOverlay(false);
    setOrderDetailsPanel(false);
    setPartyPanel(false);
    setOpenPopover(false);
    handleUpdateOrderActions(
      OrderAction.PreClosing_SettlementAgent_ReviewOrderDetailsAndPartyInformation,
      true
    );
  };

  const goSAReviewDocuments = () => {
    setShowOverlay(false);
    setOrderDetailsPanel(false);
    setPartyPanel(false);
    setDownloadPanel(false);
    setOpenPopover(false);
    handleUpdateOrderActions(
      OrderAction.PreClosing_SettlementAgent_ReviewDocumentPackage,
      true
    );
  };

  const goSAConfirmFacilitateInkSignDocs = () => {
    setShowOverlay(false);
    setOrderDetailsPanel(false);
    setPartyPanel(false);
    setDownloadPanel(false);
    setOnClosingZIndex(0);
    setOpenPopover(false);
    handleUpdateOrderActions(
      OrderAction.PreClosing_SettlementAgent_ConfirmFacilitateSigningInkSignDocuments,
      true
    );
  };

  const goPrintAllInkSignDocuments = () => {
    // Only send action and remove overlay if action hasn't been sent.
    if (
      !(
        OrderAction.PreClosing_SettlementAgent_PrintAllInkSignDocuments in
        order.Actions
      )
    ) {
      setShowOverlay(false);
      setOpenPopover(false);
      handleUpdateOrderActions(
        OrderAction.PreClosing_SettlementAgent_PrintAllInkSignDocuments,
        true
      );
    }
  };

  const handleGetExistingSettlementAgentByEmail = (saEmail: string) => {
    dispatch(fetchSettlementAgentAsync(saEmail)).then((response: any) => {
      if (response.payload) {
        setExcistingCoSa(response.payload);
        return response.payload;
      }
    });
  };

  return (
    <>
      {isFetching && (
        <div className='fetching-spinner'>
          <Loading text={isFetchingText ? isFetchingText : 'Loading'} />
        </div>
      )}
      {/* Confirm order cancellation */}
      <BasicConfirm
        open={showCancelConfirmation}
        title='Confirm order cancelation'
        showTitle
        content='Are you sure you want to cancel this order? Canceled orders cannot be reopened.'
        primaryAction={(e, a) => {
          setShowCancelConfirmation(false);
          handleCancelConfirmActions(e, a);
        }}
        primaryActionText='Confirm Order Cancelation'
        secondaryAction={(e, a) => {
          setShowCancelConfirmation(false);
          handleCancelConfirmActions(e, a);
        }}
        secondaryActionText='Do not Cancel'
      />
      {/* This is for the Upload and Tag Title Documents section */}
      <form action={tagDocsLink} ref={form} method='POST' target='thisWindow'>
        <input ref={input} type='hidden' name='access_token' value={saToken} />
      </form>
      <ProductModal
        title='Solex Doc Editor'
        open={openTagDocsModal}
        content={docTagProduct}
        onClose={openTagDocsLink}
      />
      <AddCoSettlementAgent
        open={openAddCoSa}
        existingCoSa={existingCoSa}
        orderType={order.OrderType.DocGen}
        maxCapacity={100}
        existingSettlementAgentsOnTheOrder={existingSettlementAgentsOnTheOrder}
        isAddingCoSettlement={orders.isAddingCoSettlement}
        addCosettlementAgents={(list) =>
          handleAddCosettlementAgents(list, order?.OrderType.DocGen)
        }
        getExistingSettlementAgentByEmail={(saEmail) =>
          handleGetExistingSettlementAgentByEmail(saEmail)
        }
        onClose={() => {
          setOpenAddCoSa(false);
        }}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={alertUser !== null}
        autoHideDuration={6000}
        onClose={() => {
          if (alertUser) dispatch(setAlert(null));
          if (uploadSelectError) setUploadSelectError(null);
        }}>
        <div>
          <Alert
            text={alertUser?.text || uploadSelectError}
            type={alertUser?.type}
            icon={
              alertUser?.type === 'error' ? <InfoOutlined /> : <CheckRounded />
            }
            elevated
            bold
          />
        </div>
      </Snackbar>
      {showOverlay && (
        <div
          style={{
            backgroundColor: black,
            opacity: 0.5,
            width: '100%',
            height: '100%',
            zIndex: 97,
            top: 0,
            left: 0,
            position: 'fixed',
          }}></div>
      )}
      <UploadInkSignedDocument
        open={openUploadDocs}
        onClose={() => {
          setOpenUploadDocs(false);
        }}
        isSending={orders.isFetching}
        handleFileUploader={handleUploadFiles}
        orderDocs={order.OrderDetails.Documents}
        uploadedFile={uploadedFile}
        setUploadedFile={setUploadedFile}
        fileName={fileName}
        setFileName={setFileName}
        error={error}
        setError={setError}
      />
      <ViewHistory
        onClose={() => setOpenSettlementHistory(false)}
        open={openSettlementHistory}
        data={historiesToShow}
      />
      <ViewHistory
        onClose={() => setOpenDialog(false)}
        open={openDialog}
        data={order?.Events ? order?.Events : []}
      />
      <OrderDetailsPopover
        open={openPopover}
        title={popoverContent.title}
        anchorEl={anchorEl}
        arrowRef={arrowRef}
        firstBtnText={popoverContent.firstBtnText}
        secondBtnText={popoverContent.secondBtnText}
        onClose={() => {
          setShowOverlay(false);
          setPopoverContent(initialPopoverContent);
          setOpenPopover(false);
        }}
        onInaccurateClick={() => {
          reportInaccurateInformation();
        }}
        onReviewClick={popoverContent.action}
        setArrowRef={setArrowRef}
      />
      <LoanCloser
        onClose={() => setOpenReport(false)}
        open={openReport}
        order={order}
      />
      <OrderDetailsHeader
        status={order.Status}
        orderStatus={orderStatusToShow}
        isGuest={account?.isGuest}
        branding={order.OrderDetails.Branding}
        backClicked={() => navigate('/')}
      />
      <Box sx={{ boxShadow: 3, backgroundColor: 'white' }}>
        <SolexHybrideCloseActions
          orderStatus={order.Status}
          onClosingRef={onClosingRef}
          onClosingZIndex={onClosingZIndex}
          orderType={order.OrderType}
          orderExpired={orderExpired}
          isOrderReviewedBySA={isOrderReviewedBySA}
          isDocumentReviewedBySA={isDocumentReviewedBySA}
          isAllInkSignDocumentsPrinted={isAllInkSignDocumentsPrinted}
          isFacilitateInkSignConfirmed={isFacilitateInkSignConfirmed}
          docsTagged={docsTagged}
          docsEdited={docsEdited}
          signers={order?.OrderDetails?.Signers}
          isCurrentUserEscrowOnOrder={isCurrentUserEscrowOnOrder}
          isInkSignedDocsUploadedToVault={isInkSignedDocsUploadedToVault}
          signingDate={order?.OrderDetails?.SigningDate}
          onSaActionsClick={onSaActionsClick}
        />
        <OptionalActions
          order={order}
          isCurrentUserEscrowOnOrder={isCurrentUserEscrowOnOrder}
          isCurrentUserEscrowOfficerOnOrder={
            isCurrentUserEscrowOfficerOnOrder && showAddCoSABtn
          }
          docsTagged={docsTagged}
          isThirdPartyOrder={isThirdPartyOrder}
          isInkSignedDocsUploadedToVault={isInkSignedDocsUploadedToVault}
          docsEdited={docsEdited}
          CancelableOrder={order?.Cancelable}
          orderStatus={order.Status}
          setOpenAddCoSa={setOpenAddCoSa}
          setOpenTagDocsModal={setOpenTagDocsModal}
          setOpenUploadDocs={setOpenUploadDocs}
          setShowCancelConfirmation={setShowCancelConfirmation}
        />
      </Box>
      <OrderDetailsAccordion
        order={order}
        orderDetailsPanel={orderDetailsPanel}
        partyPanel={partyPanel}
        settlementPanel={settlementPanel}
        downloadPanel={downloadPanel}
        orderDetailsRef={orderDetailsRef}
        documentRef={documentRef}
        params={params}
        isCurrentUserEscrowOnOrder={isCurrentUserEscrowOnOrder}
        orderZIndex={orderZIndex}
        documentZIndex={documentZIndex}
        setOrderDetailsPanel={setOrderDetailsPanel}
        setPartyPanel={setPartyPanel}
        setSettlementPanel={setSettlementPanel}
        setOpenDialog={setOpenDialog}
        setOpenSettlementHistory={setOpenSettlementHistory}
        setDownloadPanel={setDownloadPanel}
        goResetLogin={goResetLogin}
        getSsoToken={getToken}
        goPrintAllInkSignDocuments={goPrintAllInkSignDocuments}
      />
    </>
  );
}
